/**
 * The notification grid — four event types × email and SMS, drawn exactly as
 * `profiles-contact.png` and `profiles-main.png` draw it.
 *
 * Plan §6.2's acceptance test turns on one distinction: a link either inherits
 * the contact's defaults or overrides them, and an override with every box off
 * is not the same as inheriting. So switching a box while inheriting copies
 * the defaults down first rather than starting from empty — otherwise ticking
 * one box would silently switch the other three off.
 *
 * Which state it is in is no longer written under the grid. It was a line of
 * height on every card saying "Using defaults", and the thing it enabled —
 * going back to them — is the card's ⋮ now.
 */
import {
  NOTIFICATION_CHANNELS, NOTIFICATION_CHANNEL_LABELS, NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_EVENTS, channelAllowed,
  type NotificationChannel, type NotificationEvent, type NotificationGrid,
} from '@rowted/shared'
import { Checkbox, SectionLabel } from './primitives.js'
import { IconEmail, IconMail, IconSms } from '../frame/icons.js'

/** The same glyphs as the nav rail, at the same stroke. */
const CHANNEL_ICONS: Record<NotificationChannel, (props: { size?: number }) => JSX.Element> = {
  email: IconEmail,
  sms: IconSms,
  mail: IconMail,
}

export interface NotificationGridProps {
  /** What actually applies — the link's own grid, or the contact's defaults. */
  grid: NotificationGrid
  label?: string
  readOnly?: boolean
  onChange?: (next: NotificationGrid) => void
}

export function NotificationGridEditor({
  grid, label = 'Gets notified about', readOnly, onChange,
}: NotificationGridProps) {
  const set = (event: NotificationEvent, channel: NotificationChannel, value: boolean) => {
    if (!onChange) return
    // Copy the whole grid, not just this cell: an inheriting link that is being
    // edited becomes an override of what it was already showing.
    onChange({
      ...grid,
      [event]: { ...grid[event], [channel]: value },
    })
  }

  return (
    <div>
      {/*
        * The three glyphs sit on the header line rather than in a row of their
        * own, so the tick boxes start directly under "Gets notified about".
        * On a card that already carries a name, some tags and two lines of
        * detail, that row was a whole line of height saying nothing the icons
        * do not say where they are now.
        */}
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th scope="col" className="w-full pb-1 text-left">
              <SectionLabel>{label}</SectionLabel>
            </th>
            {NOTIFICATION_CHANNELS.map((channel) => {
              const Icon = CHANNEL_ICONS[channel]
              return (
                <th
                  key={channel}
                  scope="col"
                  className="w-[34px] pb-1 text-center font-normal text-text-muted"
                  title={NOTIFICATION_CHANNEL_LABELS[channel]}
                >
                  <span className="flex justify-center"><Icon size={15} /></span>
                  <span className="sr-only">{NOTIFICATION_CHANNEL_LABELS[channel]}</span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {NOTIFICATION_EVENTS.map((event) => (
            <tr key={event}>
              <th scope="row" className="py-[3px] text-left text-cell font-normal text-text">
                {NOTIFICATION_EVENT_LABELS[event]}
              </th>
              {NOTIFICATION_CHANNELS.map((channel) => (
                <td key={channel} className="py-[3px] text-center">
                  {/*
                    * A channel this event cannot use gets no box at all, not a
                    * disabled one: a greyed tick box invites somebody to work
                    * out why it will not tick. Posting a visit reminder is not
                    * a thing that can be arranged.
                    */}
                  {channelAllowed(event, channel) && (
                    /* aria-label rather than `label`, which Checkbox draws as
                       visible text — the column is a 34px tick box. */
                    <Checkbox
                      checked={grid[event][channel]}
                      disabled={readOnly}
                      onChange={(e) => set(event, channel, e.target.checked)}
                      aria-label={
                        `${NOTIFICATION_EVENT_LABELS[event]} by ${NOTIFICATION_CHANNEL_LABELS[channel].toLowerCase()}`
                      }
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  )
}

/**
 * The card's ⋮ item for a link that is overriding its contact's defaults.
 *
 * Here rather than in the grid because it belongs to the record, not to the
 * tick boxes: it is one of the things you can do to this link, and the other
 * things you can do to a link live in that menu too.
 */
export function notificationReset(
  overridden: boolean, onReset?: () => void,
): { key: string; label: string; onSelect: () => void }[] {
  if (!overridden || !onReset) return []
  return [{
    key: 'reset-notifications',
    label: 'Reset notifications to default',
    onSelect: onReset,
  }]
}
